import cx from 'classnames';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {shallowEqual, useSelector} from 'react-redux';
import {selectEventOptionsDesign} from '~/CreateFabricInvitation/reducers/eviteEvent/selectors';
import {GoogleAdBanner} from '~/common/fabric/GoogleAds/GoogleAdBanner';
import {webViewModalShowing} from '~/common/fabric/hooks';
import {selectIsHostViewing} from '~/common/fabric/reducers/eviteEvent/selectors';
import {selectIsSavingRegistries} from '~/common/fabric/reducers/giftingOptions/selectors';
import {UPSELL_MODES, UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {selectIsHost} from '~/common/fabric/User/reducers/user';
import {isAllowedVideoFormat} from '~/common/fabric/utils';
import {getUsedAsTypeName, isSent} from '~/common/utils/event_utils';
import {RestoreScrollPosition} from '~/ViewInvitation/common/RestoreScrollPosition';
import {StickyEditEvent} from '~/ViewInvitation/common/StickyEditEvent/StickyEditEvent';
import {ViewInvitationTabs} from '~/ViewInvitation/common/ViewInvitationTabs';
import {GiftingModal} from '~/ViewInvitation/routes/Landing/GiftingModule/GiftingModal';
import {Modules} from '~/ViewInvitation/routes/Landing/Modules';
import {hasAds, isPreview} from '~/ViewInvitation/utils';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {Video} from '~library/atoms/Video';
import {useIsLargishAndUp, useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {silver} from '~libSass/base/_exports.sass';
import {DesignTemplate} from './DesignTemplate';

const LEADERBOARD_AD_ID = 0;

export const LandingPage = (props) => {
  const {
    hideDesign,
    hideRSVP,
    hideTabs,
    hidePreviewBanner: hidePreviewBannerOverride = false,
    readonly,
    showReturnToEdit,
    onReturnToEdit,
    connectCTA,
    toggleUpsellModal,
    contentPanelWidth = 0,
    contentPanelHeight = 0,
    ctasToForceShow = [],
    isViewInvitationPage = false,
    scrollToPhotos = false,
    openPhotoGallery = false,
    openPhotoViewer = false,
    createFlow = false,
  } = props;
  const isMediumAndUp = useIsMediumAndUp();
  const isLargishAndUp = useIsLargishAndUp();
  const isSavingRegistries = useSelector(selectIsSavingRegistries);

  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [revealIsLoading, setRevealIsLoading] = useState(true);
  const {reveal_id: revealId} = useSelector(selectEventOptionsDesign, shallowEqual) || {};
  const {isPremium} = useSelector((store) => store.eventDetails);
  const displayOrder = useSelector(({moduleOrder: mo}) => mo.displayOrder).slice();
  if (!displayOrder?.includes('gifting') && isSavingRegistries) {
    displayOrder.push('gifting');
  }
  const backgroundRef = useRef(null);
  const [showingRSVPModal, setShowingRSVPModal] = useState(false);

  const isHostViewing = useSelector(selectIsHostViewing);
  const isHost = useSelector(selectIsHost);
  const usedAs = useSelector((state) => state.eventDetails?.usedAs || state.eviteEvent?.used_as);
  const eventStatus = useSelector(
    (state) => state.eventDetails?.status || state.eviteEvent?.status
  );
  const hasRsvpd = useSelector((store) => store.rsvp.hasRsvpd);
  const colors = useSelector(({coverData: cd}) => cd?.template?.colors, shallowEqual) || ['white'];
  const background = useSelector((state) => state.premiumDesign.background);
  const ctaWordings = getUsedAsTypeName(usedAs);
  if (hasRsvpd) displayOrder.push('settings');
  const isCreateEditPreview = !!isPreview();
  const showAbovePageLeaderboardGoogleAd = hasAds() && !isPremium && !isCreateEditPreview; // real view invitation only
  const showInPageLeaderboardGoogleAd = !isPremium && isCreateEditPreview; // everywhere else due to headers & desired bg colors
  const searchParams = new URLSearchParams(window.location.search);
  const isGuestPreview = !!searchParams.get('preview');
  const hidePreviewBanner = hidePreviewBannerOverride || (!isGuestPreview && !isCreateEditPreview);
  const backgroundImage = background?.imageDesktop
    ? // biome-ignore lint/style/noNestedTernary: no nested ternary
      isLargishAndUp
      ? background?.imageDesktop
      : background?.imageMobile
    : null;
  const isVideo = isPremium && isAllowedVideoFormat(backgroundImage);
  const [googleAdsKey, setGoogleAdsKey] = useState(crypto.randomUUID());
  const [adsPageType, setAdsPageType] = useState(null);
  let backgroundStyles;
  if (!isVideo)
    backgroundStyles = {
      backgroundColor: isPremium && background?.color ? background?.color : silver,
      backgroundImage: isPremium && backgroundImage ? `url(${backgroundImage})` : null,
    };

  useEffect(() => {
    if (scrollToPhotos) {
      document.body
        .querySelector('.photos__container')
        ?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }, [scrollToPhotos]);

  // We don't have time to refactor this cuz we're rushing but this needs to be refactored
  useEffect(() => {
    if (backgroundRef.current && contentPanelWidth) {
      if (isVideo) {
        if (contentPanelWidth && !isViewInvitationPage && isMediumAndUp) {
          backgroundRef.current.style.left = `${contentPanelWidth / 2}px`;
        } else {
          backgroundRef.current.style.left = '50%';
        }
      } else if (!isViewInvitationPage && isMediumAndUp) {
        if (isMediumAndUp) {
          backgroundRef.current.style.width = `${contentPanelWidth}px`;
        } else {
          backgroundRef.current.style.height = `${contentPanelHeight}px`;
        }
      }
    }
  }, [contentPanelWidth, isViewInvitationPage, isMediumAndUp, isVideo, contentPanelHeight]);

  useEffect(() => {
    const locationHash = window.location.hash;
    let timeout;

    if (locationHash.includes('module')) {
      const element = document.querySelector(locationHash);
      if (element) {
        timeout = setTimeout(() => element.scrollIntoView({behavior: 'smooth'}), 1000);
      }
    }

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const eventId = useSelector((state) => state.eviteEvent?.id);
  const removeAdsPostSend = useCallback(() => {
    window.location.href = `/invitation/${eventId}/review?upgrade=true`;
  }, [eventId]);

  const toggleUpsellModalViewInvitation = useCallback(
    (show) => {
      if (toggleUpsellModal) toggleUpsellModal(show, UPSELL_MODES.REMOVE_ADS);
      else {
        if (show) {
          webViewModalShowing('upsell');
        }
        setShowUpsellModal(show);
      }
    },
    [toggleUpsellModal]
  );

  const removeAdsCallback = useCallback(() => {
    if (isSent(eventStatus)) {
      removeAdsPostSend();
    }
  }, [eventStatus, removeAdsPostSend]);

  const renderAd = useCallback(
    (adElement, target) => {
      if (createFlow) return adElement;
      return createPortal(adElement, target);
    },
    [createFlow]
  );

  const modules = useMemo(
    () => (
      <Modules
        displayOrder={displayOrder}
        isCreateEditPreview={isCreateEditPreview}
        connectCTA={connectCTA}
        ctasToForceShow={ctasToForceShow}
        readonly={readonly}
        openPhotoGallery={openPhotoGallery}
        openPhotoViewer={openPhotoViewer}
        toggleUpsellModal={toggleUpsellModalViewInvitation}
        googleAdsKey={googleAdsKey}
        adsPageType={adsPageType}
      />
    ),
    [
      googleAdsKey,
      adsPageType,
      displayOrder,
      isCreateEditPreview,
      connectCTA,
      ctasToForceShow,
      readonly,
      openPhotoGallery,
      openPhotoViewer,
      toggleUpsellModalViewInvitation,
    ]
  );

  return (
    <>
      {!!revealId && revealIsLoading && isPremium && (
        <div className="loading-reveal-container">
          <div className="loading-reveal">
            <span className="evt-spinner inline no-margin" />
          </div>
        </div>
      )}
      {showAbovePageLeaderboardGoogleAd
        ? renderAd(
            <GoogleAdBanner
              index={LEADERBOARD_AD_ID}
              isCreateEditPreview={isCreateEditPreview}
              isGuestPreview={isGuestPreview}
              removeOnClick={toggleUpsellModalViewInvitation}
              showRemoveButton={isHost || isHostViewing}
              location="top"
              key={googleAdsKey}
              pageType={adsPageType}
            />,
            document.getElementById('view-invitation__top-banner')
          )
        : null}
      <RestoreScrollPosition path={window.location.pathname} />
      {!hidePreviewBanner
        ? <div className="view-invitation__preview-banner">
            <Typography
              variant="paragraph3"
              color="charcoal"
              classNames={cx(`view-invitation__preview-banner-text`, {
                'edit-preview': showReturnToEdit,
              })}
            >
              This is a preview of your {ctaWordings}.
            </Typography>
            {showReturnToEdit && (
              <Button
                id="continueBtn"
                data-qa-id="continueBtn"
                className="fabric-header__continue"
                variant="link"
                size="small"
                onClick={onReturnToEdit}
              >
                Done
              </Button>
            )}
          </div>
        : !!hideTabs || (
            <ViewInvitationTabs
              themeColor={isLargishAndUp ? colors[0] : 'white'}
              bgAlpha={0.75}
              ctaWordings={ctaWordings}
            />
          )}
      {isVideo
        ? <Video
            src={backgroundImage}
            className={`view-invitation__background-video${
              isViewInvitationPage ? ' view-invitation-page__background-video' : ''
            }`}
            ref={backgroundRef}
          />
        : <div
            className="view-invitation__background-image"
            ref={backgroundRef}
            style={backgroundStyles}
          />}
      {!isCreateEditPreview && !hideRSVP && (!revealId || !revealIsLoading || !isPremium) && (
        <StickyEditEvent
          setShowingRSVPModal={setShowingRSVPModal}
          setGoogleAdsKey={setGoogleAdsKey}
          setAdsPageType={setAdsPageType}
        />
      )}
      {showInPageLeaderboardGoogleAd
        ? renderAd(
            <GoogleAdBanner
              index={LEADERBOARD_AD_ID}
              isCreateEditPreview={isCreateEditPreview}
              isGuestPreview={isGuestPreview}
              removeOnClick={() => toggleUpsellModalViewInvitation(true, 'remove-ads')}
              showRemoveButton={isHost || isHostViewing}
              location="top"
              key={googleAdsKey}
              pageType={adsPageType}
            />,
            document.getElementById('view-invitation__top-banner')
          )
        : null}
      {!hideDesign && (
        <DesignTemplate
          themeColor={isLargishAndUp ? colors[0] : 'white'}
          bgAlpha={0.75}
          preview={!readonly}
          isViewInvitationPage={isViewInvitationPage}
          hideGoogleAd={isCreateEditPreview}
          onCanPlayAnimation={() => setRevealIsLoading(false)}
          toggleUpsellModal={toggleUpsellModalViewInvitation}
          googleAdsKey={googleAdsKey}
          adsPageType={adsPageType}
        />
      )}
      <div className="view-invitation__modules-wrapper">{modules}</div>
      {(displayOrder.indexOf('gift_registry') >= 0 || displayOrder.indexOf('gifting') >= 0) &&
        !showingRSVPModal &&
        !isMediumAndUp && (
          <GiftingModal
            giftingType={displayOrder.indexOf('gift_registry') >= 0 ? 'registry' : 'gifting'}
          />
        )}
      {showUpsellModal && !createFlow
        ? <UpsellModal
            mode="remove-ads"
            closeOverlay={() => setShowUpsellModal(false)}
            toggleUpsellModal={() => setShowUpsellModal(!showUpsellModal)}
            hideCloseButton={false}
            onConfirmAction={removeAdsCallback}
            onDenyAction={() => setShowUpsellModal(!showUpsellModal)}
          />
        : null}
    </>
  );
};
